import { useState, useEffect, useCallback } from 'react'
import {
  SimpleGrid,
  Image,
  Text,
  Pagination,
  Select,
  Stack,
  Group,
  ActionIcon,
  Tooltip,
  Skeleton,
  Paper,
  Box,
} from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import { formatDistanceToNow } from 'date-fns'
import { de } from 'date-fns/locale'
import { usePhotos } from '../../hooks/usePhotos'
import type { PhotoForDisplay } from '../../lib/types'
import styles from './PhotoGallery.module.css'

interface PhotoGalleryProps {
  guestId?: string // Optional for admin mode
  guestPin?: string // PIN for RLS policy (guest mode only)
  onPhotoClick?: (photo: PhotoForDisplay, index: number) => void
  onDeleteClick?: (photo: PhotoForDisplay) => void
  showDeleteButton?: boolean
  showGuestName?: boolean
  // Admin mode: controlled props
  photos?: PhotoForDisplay[]
  loading?: boolean
  totalCount?: number
  page?: number
  pageSize?: number | 'all'
  totalPages?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (value: string | null) => void
}

/**
 * Photo gallery component with pagination and thumbnail grid
 * Displays photos in a responsive grid layout with pagination controls
 */
export function PhotoGallery({
  guestId,
  guestPin,
  onPhotoClick,
  onDeleteClick,
  showDeleteButton = true,
  showGuestName = false,
  // Admin mode props
  photos: controlledPhotos,
  loading: controlledLoading,
  totalCount: controlledTotalCount,
  page: controlledPage,
  pageSize: controlledPageSize,
  totalPages: controlledTotalPages,
  onPageChange: controlledOnPageChange,
  onPageSizeChange: controlledOnPageSizeChange,
}: PhotoGalleryProps) {
  const { fetchGuestPhotos, loading: hookLoading } = usePhotos()

  // Use controlled props if provided (admin mode), otherwise use internal state (guest mode)
  const isControlled = controlledPhotos !== undefined
  const [internalPhotos, setInternalPhotos] = useState<PhotoForDisplay[]>([])
  const [internalTotalCount, setInternalTotalCount] = useState(0)
  const [internalPage, setInternalPage] = useState(1)
  const [internalPageSize, setInternalPageSize] = useState<number | 'all'>(24)
  const [internalTotalPages, setInternalTotalPages] = useState(1)

  const photos = isControlled ? controlledPhotos : internalPhotos
  const totalCount = isControlled ? (controlledTotalCount ?? 0) : internalTotalCount
  const page = isControlled ? (controlledPage ?? 1) : internalPage
  const pageSize = isControlled ? (controlledPageSize ?? 24) : internalPageSize
  const totalPages = isControlled ? (controlledTotalPages ?? 1) : internalTotalPages
  const loading = isControlled ? (controlledLoading ?? false) : hookLoading

  const locale = de

  // Fetch photos when page or pageSize changes (guest mode only)
  useEffect(() => {
    if (!isControlled && guestId) {
      const loadPhotos = async () => {
        const result = await fetchGuestPhotos(
          guestId,
          {
            page: internalPage,
            pageSize: internalPageSize,
            sortBy: 'uploaded_at',
            sortDirection: 'desc',
          },
          guestPin
        )

        setInternalPhotos(result.photos)
        setInternalTotalCount(result.totalCount)
        setInternalTotalPages(result.totalPages)
      }

      loadPhotos()
    }
  }, [guestId, internalPage, internalPageSize, fetchGuestPhotos, isControlled])

  const handlePhotoClick = useCallback(
    (photo: PhotoForDisplay, index: number) => {
      if (onPhotoClick) {
        onPhotoClick(photo, index)
      }
    },
    [onPhotoClick]
  )

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent, photo: PhotoForDisplay) => {
      e.stopPropagation() // Prevent photo click when clicking delete
      if (onDeleteClick) {
        onDeleteClick(photo)
      }
    },
    [onDeleteClick]
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (isControlled && controlledOnPageChange) {
        controlledOnPageChange(newPage)
      } else {
        setInternalPage(newPage)
      }
    },
    [isControlled, controlledOnPageChange]
  )

  const handlePageSizeChange = useCallback(
    (value: string | null) => {
      const newPageSize = value === 'all' ? 'all' : parseInt(value || '24', 10)
      if (isControlled && controlledOnPageSizeChange) {
        controlledOnPageSizeChange(value)
      } else {
        setInternalPageSize(newPageSize)
        setInternalPage(1) // Reset to first page when changing page size
      }
    },
    [isControlled, controlledOnPageSizeChange]
  )

  if (loading && photos.length === 0) {
    return (
      <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, md: 4 }} spacing="md">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} height={200} radius="md" />
        ))}
      </SimpleGrid>
    )
  }

  if (photos.length === 0) {
    return (
      <Paper p="xl" radius="md" className={styles.emptyState}>
        <Text ta="center" c="dimmed" size="lg">
          Noch keine Fotos hochgeladen
        </Text>
        <Text ta="center" c="dimmed" size="sm" mt="xs">
          Lade deine ersten Erinnerungen hoch!
        </Text>
      </Paper>
    )
  }

  return (
    <Stack gap="md">
      {/* Pagination Controls */}
      <Group justify="space-between" align="center" wrap="wrap">
        <Text size="sm" c="dimmed">
          Zeige {photos.length} von {totalCount} Fotos
        </Text>

        <Group gap="sm">
          <Select
            value={pageSize === 'all' ? 'all' : String(pageSize)}
            onChange={handlePageSizeChange}
            data={[
              { value: '12', label: '12' },
              { value: '24', label: '24' },
              { value: '48', label: '48' },
              { value: 'all', label: 'Alle' },
            ]}
            size="sm"
            style={{ width: 100 }}
            aria-label="Fotos pro Seite"
          />
        </Group>
      </Group>

      {/* Photo Grid */}
      <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, md: 4 }} spacing="md">
        {photos.map((photo, index) => (
          <Box
            key={photo.id}
            className={styles.photoContainer}
            onClick={() => handlePhotoClick(photo, index)}
          >
            <Paper className={styles.photoThumbnail} shadow="sm" radius="md" p={0}>
              <div className={styles.imageWrapper}>
                <Image
                  src={photo.thumbnailUrl}
                  alt={photo.filename}
                  className={styles.image}
                  loading="lazy"
                />
                {showDeleteButton && (
                  <Tooltip label="Foto löschen">
                    <ActionIcon
                      className={styles.deleteButton}
                      color="red"
                      variant="filled"
                      size="lg"
                      onClick={(e) => handleDeleteClick(e, photo)}
                      aria-label="Foto löschen"
                    >
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </div>

              <Stack gap={4} p="xs">
                {showGuestName && photo.guestName && (
                  <Text size="xs" fw={600} truncate>
                    {photo.guestName}
                  </Text>
                )}
                <Text size="xs" c="dimmed">
                  {formatDistanceToNow(photo.uploadedAt, {
                    addSuffix: true,
                    locale,
                  })}
                </Text>
              </Stack>
            </Paper>
          </Box>
        ))}
      </SimpleGrid>

      {/* Pagination */}
      {totalPages > 1 && (
        <Group justify="center" mt="md">
          <Pagination
            value={page}
            onChange={handlePageChange}
            total={totalPages}
            size="sm"
            radius="md"
            aria-label="Seitennavigation"
          />
        </Group>
      )}
    </Stack>
  )
}
